import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import MonthYearPicker from './MonthYearPicker';
import { getCalendar, listSchedulableClasses, createSessionAny, updateSession, cancelSession,
  completeSession, getSessionRoster, markAttendance,
  listInstructors, listClosures, addClosure, removeClosure, setBlockWeekends } from './api';

// Local HH:MM (24h) for a UTC instant rendered in a specific zone, for time inputs.
const zonedTime = (iso, tz) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz }) : '');

const iso = (d) => d.toISOString().slice(0, 10);
const monthStart = (y, m) => new Date(y, m, 1);
const monthEnd = (y, m) => new Date(y, m + 1, 0);
const ZONES = [
  ['America/New_York', 'Eastern'], ['America/Chicago', 'Central'], ['America/Denver', 'Mountain'],
  ['America/Phoenix', 'Arizona'], ['America/Los_Angeles', 'Pacific'], ['America/Anchorage', 'Alaska'],
  ['Pacific/Honolulu', 'Hawaii'],
];

export default function SchedulingCalendar({ canEdit = false }) {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [data, setData] = useState({ sessions: [], tasks: [], holidays: [] });
  const [classes, setClasses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [filters, setFilters] = useState({ class_id: '', instructor_id: '' });
  const [newFor, setNewFor] = useState(null); // Date for the create modal
  const [editing, setEditing] = useState(null); // session object for the edit modal
  const [showClosures, setShowClosures] = useState(false);

  const from = monthStart(cursor.y, cursor.m);
  const to = monthEnd(cursor.y, cursor.m);

  const load = () => getCalendar(iso(from), iso(to), {
    ...(filters.class_id ? { class_id: filters.class_id } : {}),
    ...(filters.instructor_id ? { instructor_id: filters.instructor_id } : {}),
  }).then(setData).catch(() => toast.error('Could not load calendar'));

  useEffect(() => { load(); }, [cursor.y, cursor.m, filters.class_id, filters.instructor_id]);
  useEffect(() => {
    listSchedulableClasses().then((r) => setClasses(r.classes || [])).catch(() => {});
    listInstructors().then((r) => setInstructors(r.instructors || [])).catch(() => {});
  }, []);

  const holidayLabels = useMemo(() => {
    const m = {};
    (data.holidays || []).forEach((h) => { m[h.on] = h.label; });
    return m;
  }, [data.holidays]);
  const byDay = useMemo(() => {
    const map = {};
    // Bucket sessions on their session-zone local_date (from the API), not the UTC
    // instant, so an evening session lands on the correct calendar day.
    (data.sessions || []).forEach((s) => { const k = s.local_date || s.starts_at.slice(0, 10); (map[k] ||= []).push({ ...s, _t: 'session' }); });
    (data.tasks || []).forEach((t) => { const k = (t.due_date || '').slice(0, 10); (map[k] ||= []).push({ ...t, _t: 'task' }); });
    return map;
  }, [data]);

  const cells = useMemo(() => {
    const lead = from.getDay();
    const days = to.getDate();
    const arr = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= days; d++) arr.push(new Date(cursor.y, cursor.m, d));
    return arr;
    // from/to derive from cursor; depend on the primitives to avoid per-render churn.
  }, [cursor.y, cursor.m]);

  const move = (delta) => {
    const m = cursor.m + delta;
    setCursor({ y: cursor.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 });
  };
  const goToday = () => setCursor({ y: today.getFullYear(), m: today.getMonth() });

  const monthLabel = from.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  const todayKey = iso(today);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => move(-1)}
            title="Previous month"
            aria-label="Previous month"
            className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ChevronLeft size={16} />
          </button>
          {/* The label is the jump control: stepping a year at a time meant
              twelve clicks to reach next March. */}
          <MonthYearPicker value={cursor} onChange={setCursor} today={today} label={monthLabel} />
          <button
            onClick={() => move(1)}
            title="Next month"
            aria-label="Next month"
            className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ChevronRight size={16} />
          </button>
          <button
            onClick={goToday}
            className="ml-1 px-2.5 py-1.5 rounded-lg border border-blue-300 dark:border-blue-500/40 text-blue-600 dark:text-blue-400 text-sm hover:bg-blue-50 dark:hover:bg-blue-500/10"
          >
            Today
          </button>
        </div>
        <select value={filters.class_id} onChange={(e) => setFilters((f) => ({ ...f, class_id: e.target.value }))}
          className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-1.5">
          <option value="">All classes</option>
          {classes.map((c) => <option key={`${c.kind}-${c.id}`} value={c.id}>{c.title}</option>)}
        </select>
        <select value={filters.instructor_id} onChange={(e) => setFilters((f) => ({ ...f, instructor_id: e.target.value }))}
          className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-1.5">
          <option value="">All instructors</option>
          {instructors.map((i) => <option key={i.id} value={i.id}>{i.name || i.email}</option>)}
        </select>
        {canEdit && <button onClick={() => setShowClosures(true)} className="text-sm text-blue-600 ml-auto">Manage closures</button>}
      </div>

      <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-xl overflow-hidden">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="bg-white dark:bg-gray-900 text-[11px] font-semibold text-gray-500 p-2 text-center">{d}</div>
        ))}
        {cells.map((date, i) => {
          if (!date) return <div key={`e${i}`} className="bg-gray-50 dark:bg-gray-950 min-h-[104px]" />;
          const key = iso(date);
          const holidayLabel = holidayLabels[key];
          const isToday = key === todayKey;
          const items = byDay[key] || [];
          return (
            <div key={key}
              onClick={() => canEdit && !holidayLabel && setNewFor(date)}
              className={`group relative bg-white dark:bg-gray-900 min-h-[104px] p-1.5 ${
                canEdit && !holidayLabel ? 'cursor-pointer hover:bg-blue-50/60 dark:hover:bg-gray-800' : ''} ${
                holidayLabel ? 'bg-gray-50 dark:bg-gray-950' : ''}`}>
              <div className="flex items-center justify-between">
                <span className={isToday
                  ? 'text-[11px] font-bold text-white bg-blue-600 rounded-full w-5 h-5 flex items-center justify-center'
                  : 'text-[11px] text-gray-400'}>{date.getDate()}</span>
                {canEdit && !holidayLabel && (
                  <span className="text-[10px] font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition">+ New</span>
                )}
              </div>
              {holidayLabel && (
                <div className="mt-0.5 text-[10px] text-orange-700 dark:text-orange-400 font-medium leading-tight" title={`Closed: ${holidayLabel}`}>
                  {holidayLabel}
                </div>
              )}
              {items.map((it, idx) => (
                <div key={idx}
                  onClick={(e) => { e.stopPropagation(); if (canEdit && it._t === 'session') setEditing(it); }}
                  className={`mt-0.5 truncate text-[10px] rounded px-1 py-0.5 ${
                    canEdit && it._t === 'session' ? 'cursor-pointer' : ''} ${
                    it._t !== 'session' ? 'bg-blue-100 text-blue-800'
                      : it.status === 'canceled' ? 'bg-gray-200 text-gray-500 line-through'
                      : it.status === 'completed' ? 'bg-green-100 text-green-800'
                      : 'bg-orange-100 text-orange-800'}`}
                  title={it._t === 'session' ? `${it.title} (${new Date(it.starts_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: it.time_zone })})` : it.title}>
                  {it._t === 'session'
                    ? `${new Date(it.starts_at).toLocaleTimeString([], { hour: 'numeric', timeZone: it.time_zone })} ${it.title}`
                    : `• ${it.title}`}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {newFor && (
        <SessionModal mode="new" date={newFor} classes={classes} instructors={instructors}
          onClose={() => setNewFor(null)} onSaved={() => { setNewFor(null); load(); }} />
      )}
      {editing && (
        <SessionModal mode="edit" session={editing} instructors={instructors}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {showClosures && <ClosuresModal year={cursor.y} onClose={() => { setShowClosures(false); load(); }} />}
    </div>
  );
}

const FIELD = 'mt-1 w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2';

// Unified create/edit session modal. mode 'new' needs `date` + `classes`; mode
// 'edit' needs `session` (a calendar session object) and adds cancel/complete/roster.
function SessionModal({ mode, date, session, classes = [], instructors, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(() => (isEdit ? {
    class_ref: '',
    date: session.local_date || (session.starts_at || '').slice(0, 10),
    time: zonedTime(session.starts_at, session.time_zone) || '10:00',
    endTime: zonedTime(session.ends_at, session.time_zone),
    time_zone: session.time_zone || 'America/New_York',
    modality: session.modality || 'virtual',
    virtual_link: session.virtual_link || '', location: session.location || '',
    instructor_id: session.instructor?.id ? String(session.instructor.id) : '',
    capacity: session.capacity != null ? String(session.capacity) : '',
  } : {
    class_ref: '', date: date.toISOString().slice(0, 10), time: '10:00', endTime: '',
    time_zone: 'America/New_York', modality: 'virtual', virtual_link: '', location: '',
    instructor_id: '', capacity: '',
  }));
  const [busy, setBusy] = useState(false);
  const [roster, setRoster] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (isEdit) getSessionRoster(session.id).then(setRoster).catch(() => setRoster({ roster: [] }));
  }, [isEdit, session?.id]);

  const commonAttrs = () => ({
    starts_at: `${form.date}T${form.time}`,
    ends_at: form.endTime ? `${form.date}T${form.endTime}` : null,
    time_zone: form.time_zone, modality: form.modality,
    virtual_link: form.virtual_link, location: form.location,
    instructor_id: form.instructor_id || null,
    capacity: form.capacity ? parseInt(form.capacity, 10) : null,
  });

  const submit = async (override = false, overrideReason = '') => {
    let call;
    if (isEdit) {
      call = () => updateSession(session.id, { ...commonAttrs(), override, override_reason: overrideReason });
    } else {
      const cls = classes.find((c) => String(c.id) === String(form.class_ref));
      if (!cls) { toast.error('Pick a class'); return; }
      const parentKey = cls.kind === 'catalog' ? 'zu_content_item_id' : 'training_concept_id';
      call = () => createSessionAny({ ...commonAttrs(), [parentKey]: cls.id, override, override_reason: overrideReason });
    }
    setBusy(true);
    try {
      await call();
      toast.success(isEdit ? 'Session updated' : 'Session scheduled');
      onSaved();
    } catch (e) {
      const msg = e?.message || 'Could not save';
      if (/Cannot schedule/i.test(msg) && window.confirm(`${msg}\n\nOverride anyway?`)) {
        const reason = window.prompt('Override reason (recorded):', 'Approved exception');
        if (reason) return submit(true, reason);
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    if (!window.confirm('Cancel this session and notify registered attendees?')) return;
    try { await cancelSession(session.id); toast.success('Session canceled'); onSaved(); }
    catch (e) { toast.error(e?.message || 'Cancel failed'); }
  };
  const doComplete = async () => {
    try { await completeSession(session.id); toast.success('Session marked complete'); onSaved(); }
    catch (e) { toast.error(e?.message || 'Could not complete'); }
  };
  const mark = async (registration_id, status) => {
    try { await markAttendance(session.id, { registration_id, status }); const r = await getSessionRoster(session.id); setRoster(r); }
    catch (e) { toast.error(e?.message || 'Could not update attendance'); }
  };

  const heading = isEdit ? (session.title || 'Session') : `New session - ${date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`;
  const closed = isEdit && session.status !== 'scheduled';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-[480px] max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-lg font-bold text-blue-900 dark:text-blue-200">
            {heading}{isEdit && closed && <span className="ml-2 text-xs font-normal text-gray-400">({session.status})</span>}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-3">
          {!isEdit && (
            <label className="block text-xs text-gray-500">Class
              <select value={form.class_ref} onChange={(e) => set('class_ref', e.target.value)} className={FIELD}>
                <option value="">Select a class...</option>
                {classes.map((c) => <option key={`${c.kind}-${c.id}`} value={c.id}>{c.title}</option>)}
              </select></label>
          )}
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-gray-500">Date
              <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className={FIELD} /></label>
            <label className="text-xs text-gray-500">Time zone
              <select value={form.time_zone} onChange={(e) => set('time_zone', e.target.value)} className={FIELD}>
                {ZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <label className="text-xs text-gray-500">Start
              <input type="time" value={form.time} onChange={(e) => set('time', e.target.value)} className={FIELD} /></label>
            <label className="text-xs text-gray-500">End (optional)
              <input type="time" value={form.endTime} onChange={(e) => set('endTime', e.target.value)} className={FIELD} /></label>
            <label className="text-xs text-gray-500">Instructor
              <select value={form.instructor_id} onChange={(e) => set('instructor_id', e.target.value)} className={FIELD}>
                <option value="">Unassigned</option>
                {instructors.map((i) => <option key={i.id} value={i.id}>{i.name || i.email}</option>)}
              </select></label>
            <label className="text-xs text-gray-500">Modality
              <select value={form.modality} onChange={(e) => set('modality', e.target.value)} className={FIELD}>
                <option value="virtual">Virtual</option><option value="in_person">In person</option><option value="hybrid">Hybrid</option>
              </select></label>
            <label className="text-xs text-gray-500">Capacity (optional)
              <input type="number" min="1" value={form.capacity} onChange={(e) => set('capacity', e.target.value)} className={FIELD} /></label>
            <label className="text-xs text-gray-500">Location
              <input value={form.location} onChange={(e) => set('location', e.target.value)} className={FIELD} /></label>
          </div>
          <label className="block text-xs text-gray-500">Join link
            <input value={form.virtual_link} onChange={(e) => set('virtual_link', e.target.value)} placeholder="https://..." className={FIELD} /></label>
          {instructors.length === 0 && (
            <p className="text-[11px] text-gray-400">No trainers yet. Add one in Admin -&gt; Team &amp; Roles (role: Trainer) to assign an instructor.</p>
          )}

          {isEdit && (
            <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
              <div className="text-xs font-semibold text-gray-500 mb-1">
                Roster {roster ? `(${roster.taken || 0}/${roster.capacity ?? '-'})` : ''}
              </div>
              {roster && roster.roster.length === 0 && <div className="text-xs text-gray-400">No one registered yet.</div>}
              <ul className="space-y-1 max-h-40 overflow-auto">
                {roster && roster.roster.map((r) => (
                  <li key={r.registration_id} className="flex items-center justify-between text-sm">
                    <span>{r.attendee || r.invited_email} <span className="text-xs text-gray-400">{r.status}</span></span>
                    <span className="flex gap-2">
                      <button onClick={() => mark(r.registration_id, 'attended')} className="text-xs text-green-600">attended</button>
                      <button onClick={() => mark(r.registration_id, 'no_show')} className="text-xs text-orange-600">no-show</button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-800">
          {isEdit && !closed && (
            <>
              <button onClick={doCancel} className="text-sm text-orange-600 px-2 py-2">Cancel session</button>
              <button onClick={doComplete} className="text-sm text-green-700 px-2 py-2">Mark complete</button>
            </>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="text-sm text-gray-500 px-3 py-2">Close</button>
            {!closed && (
              <button onClick={() => submit()} disabled={busy}
                className="text-sm font-semibold px-4 py-2 rounded-lg bg-orange-500 text-white disabled:opacity-50">
                {busy ? 'Saving...' : isEdit ? 'Save changes' : 'Schedule'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ClosuresModal({ year, onClose }) {
  const [closures, setClosures] = useState([]);
  const [federal, setFederal] = useState([]);
  const [blockWeekends, setBW] = useState(false);
  const [form, setForm] = useState({ on: '', label: '' });

  const load = () => listClosures(year).then((r) => {
    setClosures(r.closures || []); setFederal(r.federal_holidays || []); setBW(!!r.block_weekends);
  }).catch(() => {});
  useEffect(() => { load(); }, [year]);

  const toggleWeekends = async (enabled) => {
    setBW(enabled);
    try { await setBlockWeekends(enabled); }
    catch (e) { toast.error(e?.message || 'Could not save'); setBW(!enabled); }
  };

  const add = async () => {
    if (!form.on) return;
    try { await addClosure(form.on, form.label); setForm({ on: '', label: '' }); load(); }
    catch (e) { toast.error(e?.message || 'Could not add'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-[460px] max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h3 className="text-lg font-bold text-blue-900 dark:text-blue-200">Closures &amp; holidays - {year}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-5">
          <label className="flex items-center justify-between rounded-xl border border-blue-100 dark:border-gray-700 p-3 cursor-pointer">
            <span className="text-sm font-medium">Block weekends (Sat + Sun)</span>
            <input type="checkbox" checked={blockWeekends} onChange={(e) => toggleWeekends(e.target.checked)}
              className="h-4 w-4 accent-orange-500" />
          </label>

          <div className="rounded-xl border border-blue-100 dark:border-gray-700 p-3 space-y-2">
            <div className="text-xs font-semibold text-gray-500">Add a company closure</div>
            <div className="grid grid-cols-[auto,1fr] gap-2">
              <input type="date" value={form.on} onChange={(e) => setForm((f) => ({ ...f, on: e.target.value }))}
                className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 min-w-0" />
              <input placeholder="Label (e.g. Office closed)" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 min-w-0" />
            </div>
            <button onClick={add} disabled={!form.on}
              className="w-full text-sm font-semibold py-2 rounded-lg bg-orange-500 text-white disabled:opacity-50">
              Add closure
            </button>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">Company closures</div>
            {closures.length === 0 && <div className="text-xs text-gray-400">None yet.</div>}
            <ul className="space-y-1">
              {closures.map((c) => (
                <li key={c.id} className="flex items-center justify-between text-sm rounded-lg px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <span>{c.on}{c.label ? ` - ${c.label}` : ''}</span>
                  <button onClick={() => removeClosure(c.id).then(load)} className="text-xs text-orange-600">remove</button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">US federal holidays (automatic)</div>
            <ul className="space-y-1">
              {federal.map((h) => (
                <li key={h.on} className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
                  <span>{h.label}</span>
                  <span className="text-xs text-gray-400">{h.on}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
